import isEmpty from 'lodash/isEmpty'
import * as React from 'react'

import { LOCIZE_NAMESPACE } from 'main/common'
import ChildrenSelector, { IChildEditState } from 'main/components/child/ChildrenSelector'
import Divider from 'main/components/common/Divider'
import DesiredWeeklyScheduleFields, {
  IDesiredWeeklyScheduleParams,
} from 'main/components/family/DesiredWeeklyScheduleFields'
import { IChild } from 'main/gql/child'
import { ITour } from 'main/gql/tour'
import { Button, NativeLink, spacing, Strong, Text, typography } from 'main/ui'
import { momentFromDateAndTime } from 'main/util/moment'
import { TFunction, useTranslation } from 'react-i18next'
import { i18nKeyPrefix } from '../../../i18n'
import { ScheduleTourModal_school, ScheduleTourModal_tourOpenings } from '../../common/gql'
import ProgressBar from '../../common/ProgressBar'
import SchoolSummary from '../../common/SchoolSummary'
import { IValidation } from './types'
import { actualSelectedChildren, initialChildEditStates, validate } from './util'

const PRE = `${i18nKeyPrefix}-SCHEDULE-MODAL-DETAILS`

interface IProps {
  children: IChild[]
  school: ScheduleTourModal_school
  tour: ITour | null
  initialSelectedChildren: IChild[]
  selectedOpening: ScheduleTourModal_tourOpenings
  desiredWeeklyScheduleParams: IDesiredWeeklyScheduleParams
  onChangeDesiredWeeklyScheduleParams: React.Dispatch<
    React.SetStateAction<IDesiredWeeklyScheduleParams>
  >
  onComplete: (selectedChildren: IChild[]) => void
  onRequestOpeningSelection: () => void
}

export default function Review(props: IProps) {
  const {
    children,
    initialSelectedChildren,
    school,
    selectedOpening,
    desiredWeeklyScheduleParams,
    onChangeDesiredWeeklyScheduleParams,
    onComplete,
    onRequestOpeningSelection,
  } = props
  const { t } = useTranslation(LOCIZE_NAMESPACE)

  const [validation, setValidation] = React.useState<IValidation>({})
  const [existingChildren, setExistingChildren] = React.useState<IChild[]>(children)
  const [selectedChildren, setSelectedChildren] = React.useState<IChild[]>(initialSelectedChildren)
  const [childEditStates, setChildEditStates] = React.useState<IChildEditState[]>(
    initialChildEditStates(children),
  )

  const selectedOpeningMoment = momentFromDateAndTime(
    selectedOpening.date,
    selectedOpening.beginsAt,
    school.timezone,
  )

  return (
    <div>
      <SchoolSummary school={school} />
      <div css={{ marginTop: spacing.majorScale(1) }}>
        <ProgressBar progress={90} />
      </div>

      <div css={{ marginTop: spacing.majorScale(2) }}>
        <Strong>{t(`${PRE}-TOURING`)}</Strong>
        <Text>{selectedOpeningMoment.format('dddd, MMMM D YYYY · h:mma')}&nbsp;&nbsp;</Text>
        <NativeLink
          css={{ fontWeight: typography.fontWeight.bold }}
          onClick={onRequestOpeningSelection}
        >
          {t(`${PRE}-LINK`)}
        </NativeLink>
      </div>

      <Divider margin={spacing.minorScale(5)} />

      <div css={{ marginBottom: spacing.majorScale(1) }}>
        <Strong>{t(`${PRE}-SCHEDULE-SECTION`)}</Strong>
        <div css={{ marginTop: spacing.majorScale(2) }}>
          <DesiredWeeklyScheduleFields
            params={desiredWeeklyScheduleParams}
            validationHints={validation.desiredWeeklySchedule || {}}
            onChangeDays={days =>
              onChangeDesiredWeeklyScheduleParams(currentParams => ({
                ...currentParams,
                days: { ...currentParams.days, ...days },
              }))
            }
            onChangeDropoffAt={dropoffAt =>
              onChangeDesiredWeeklyScheduleParams(currentParams => ({
                ...currentParams,
                dropoffAt,
              }))
            }
            onChangePickupAt={pickupAt =>
              onChangeDesiredWeeklyScheduleParams(currentParams => ({
                ...currentParams,
                pickupAt,
              }))
            }
          />
        </div>
      </div>

      <Divider margin={spacing.minorScale(5)} />

      <Strong>{t(`${PRE}-CHILD-SECTION`)}</Strong>
      <div css={{ marginTop: spacing.majorScale(2) }}>
        <ChildrenSelector
          children={existingChildren}
          selectedChildren={selectedChildren}
          editStates={childEditStates}
          validationHint={validation.selectedChildren}
          onAddChild={child => {
            setExistingChildren(prev => prev.concat(child))
            setSelectedChildren(prev => prev.concat(child))
          }}
          onUpdateChild={child => {
            setExistingChildren(prev => prev.map(c => (c.id === child.id ? child : c)))
            setSelectedChildren(prev =>
              selectedChildren.find(c => c.id === child.id) ? prev : prev.concat(child),
            )
          }}
          onChangeSelectedChildren={setSelectedChildren}
          onChangeEditStates={setChildEditStates}
        />
      </div>

      <div css={{ marginTop: spacing.majorScale(4) }}>
        <Button
          full
          onClick={() =>
            handleSubmit(
              desiredWeeklyScheduleParams,
              existingChildren,
              selectedChildren,
              childEditStates,
              setValidation,
              onComplete,
              t,
            )
          }
        >
          {t(`${PRE}-BUTTON`)}
        </Button>
      </div>
    </div>
  )
}

function handleSubmit(
  desiredWeeklyScheduleParams: IDesiredWeeklyScheduleParams,
  children: IChild[],
  selectedChildren: IChild[],
  childEditStates: IChildEditState[],
  setValidation: (validation: IValidation) => void,
  onComplete: (selectedChildren: IChild[]) => void,
  t: TFunction,
) {
  const validation = validate(
    desiredWeeklyScheduleParams,
    children,
    selectedChildren,
    childEditStates,
    t,
  )

  if (isEmpty(validation)) {
    onComplete(actualSelectedChildren(selectedChildren, childEditStates))
  } else {
    setValidation(validation)
  }
}
